import React from 'react'
import cx from 'classnames'
import {
  Radar,
  RadarChart as RechartsRadarChart,
  PolarGrid,
  PolarAngleAxis,
  PolarRadiusAxis,
  ResponsiveContainer,
} from 'recharts'
import {
  labelTranslations,
  RADAR_COLORS,
  FIXED_RADAR_LAYOUT,
} from './constants'
import styles from './RadarChart.module.scss'

import { halfString } from 'helpers'

const CustomTick = ({
  payload,
  x,
  y,
  textAnchor,
  stroke,
  radius,
  selected,
  setSelected,
  dataLength,
}) => {
  // show long labels in 2 rows
  const halvedValues =
    payload.value.length > 15 ? halfString(payload.value) : undefined

  // Safari ignores code that rounds selected label background
  // so instead labels are bigger and bolder on Safari, without
  // additional background
  const isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent)

  const translation = labelTranslations?.[dataLength]?.[payload.index] || {
    x: 0,
    y: 0,
  }

  return (
    <g
      onClick={() => {
        setSelected(payload.index)
      }}
      className={cx('recharts-layer recharts-polar-angle-axis-tick')}
    >
      <defs>
        <filter
          id={`solid${payload.index}`}
          x="-15%"
          width="130%"
          y={halvedValues ? '-25%' : '-50%'}
          height={halvedValues ? '150%' : '200%'}
        >
          <feFlood
            floodColor={selected && !isSafari ? '#006788' : 'transparent'}
          />
          <feGaussianBlur stdDeviation="7" />
          <feComponentTransfer>
            <feFuncA type="table" tableValues="0 0 0 1" />
          </feComponentTransfer>

          <feComponentTransfer>
            <feFuncA type="table" tableValues="0 1 1 1 1 1 1 1" />
          </feComponentTransfer>
          <feComposite operator="over" in="SourceGraphic" />
        </filter>
      </defs>
      <text
        radius={radius}
        stroke={stroke}
        x={x}
        y={y}
        textAnchor={textAnchor}
        filter={`url(#solid${payload.index})`}
        className={cx('recharts-text recharts-polar-angle-axis-tick-value', {
          [styles.selected]: Boolean(selected) && !isSafari,
          [styles.safariSelected]: Boolean(selected) && isSafari,
        })}
      >
        {halvedValues ? (
          <>
            <tspan
              x={x + translation.x}
              y={(payload.index ? y : y - 16) + translation.y}
              dy="0em"
            >
              {halvedValues[0]}
            </tspan>
            <tspan
              x={x + translation.x}
              y={(payload.index ? y + 16 : y) + translation.y}
              dy="0em"
            >
              {halvedValues[1]}
            </tspan>
          </>
        ) : (
          <tspan x={x + translation.x} y={y + translation.y} dy="0em">
            {payload.value}
          </tspan>
        )}
      </text>
    </g>
  )
}

type RadarChartProps = {
  data: { label: string; score?: number; score2?: number }[]
  candidates?: { id: number | string }[]
  selectedCandidates?: Record<string, boolean>
  setSelected?: (index: number) => void
  selected?: number | null
  maxHeight?: string
  isAnimationActive?: boolean
  single?: boolean
  margin?: { top?: number; right?: number; bottom?: number; left?: number }
  fixedLayout?: boolean
}

export const RadarChart = ({
  data,
  candidates,
  selectedCandidates,
  setSelected = () => {},
  selected,
  maxHeight = '100%',
  isAnimationActive = false,
  single,
  margin,
  fixedLayout = false,
}: RadarChartProps) => {
  const chartMargin =
    margin ?? (fixedLayout ? FIXED_RADAR_LAYOUT.margin : undefined)
  const chartOuterRadius = fixedLayout ? FIXED_RADAR_LAYOUT.outerRadius : '80%'

  return (
    <ResponsiveContainer
      className={cx(styles.responsiveContainer, {
        [styles.fixedLayout]: fixedLayout,
      })}
      width={fixedLayout ? '100%' : '75%'}
      height="100%"
      maxHeight={
        fixedLayout ? undefined : (maxHeight as unknown as number | undefined)
      }
    >
      <RechartsRadarChart
        cx="50%"
        cy="50%"
        outerRadius={chartOuterRadius}
        data={data}
        className={styles.radarChart}
        {...(chartMargin != null ? { margin: chartMargin } : {})}
      >
        <PolarGrid gridType="circle" />
        <PolarAngleAxis
          dataKey="label"
          className={styles.polarAngleAxis}
          tick={props => (
            <CustomTick
              {...props}
              dataLength={data.length}
              selected={props.payload.index === selected}
              setSelected={setSelected}
            />
          )}
        ></PolarAngleAxis>
        <PolarRadiusAxis className={styles.polarRadiusAxis} domain={[0, 4]} />
        {single || selectedCandidates?.[String(candidates?.[0]?.id)] ? (
          <Radar
            name="radar1"
            dataKey="score"
            stroke={
              single ? RADAR_COLORS.single.stroke : RADAR_COLORS.first.stroke
            }
            fill={single ? RADAR_COLORS.single.fill : RADAR_COLORS.first.fill}
            fillOpacity={
              single
                ? RADAR_COLORS.single.fillOpacity
                : RADAR_COLORS.first.fillOpacity
            }
            isAnimationActive={isAnimationActive}
            className={cx(styles.radar, {
              [styles.radarFirst]: !single,
            })}
            dot
          />
        ) : null}
        {!single && selectedCandidates?.[String(candidates?.[1]?.id)] ? (
          <Radar
            name="radar2"
            dataKey="score2"
            stroke={RADAR_COLORS.second.stroke}
            fill={RADAR_COLORS.second.fill}
            fillOpacity={RADAR_COLORS.second.fillOpacity}
            isAnimationActive={isAnimationActive}
            className={cx(styles.radar, styles.radarSecond)}
            dot
          />
        ) : null}
      </RechartsRadarChart>
    </ResponsiveContainer>
  )
}
